import lottie from 'lottie-web';
import { loadFonts } from '@loaders/FontLoader';

/**
 * Asset manifest for Artemis Math Mission.
 *
 * Only the static art is loaded here. Dynamic / animated elements are drawn in
 * code: the starfield, the proportional percentage bar + segment fills + red
 * wrong-indicator, the colour-matched pipe hoses, and the launch smoke.
 */
const ASSETS = [
  // backdrops
  ['starfield-a', 'ArtemisAssets/starfield-a.png'],
  ['starfield-b', 'ArtemisAssets/starfield-b.png'],
  ['bg-launchpad', 'ArtemisAssets/bg-launchpad.png'],
  ['overlay-dim', 'ArtemisAssets/overlay-dim.png'],
  ['traffic-light', 'ArtemisAssets/traffic-light.png'],
  ['score-dot-green', 'ArtemisAssets/score-dot-green.png'],
  ['sun', 'ArtemisAssets/sun.png'],
  ['earth', 'ArtemisAssets/earth.png'],
  ['flight-path', 'ArtemisAssets/flight-path.png'],
  // per-round background plates (black + stars + scenery)
  ['bg-round-1', 'ArtemisAssets/bg-round-1.png'],
  ['bg-round-2', 'ArtemisAssets/bg-round-2.png'],
  ['bg-round-3', 'ArtemisAssets/bg-round-3.png'],
  ['bg-round-4', 'ArtemisAssets/bg-round-4.png'],
  ['bg-round-5', 'ArtemisAssets/bg-round-5.png'],
  ['bg-round-6', 'ArtemisAssets/bg-round-6.png'],
  // actors
  ['rocket', 'ArtemisAssets/rocket.png'],
  ['start-rocket', 'ArtemisAssets/start-rocket.png'],
  ['pump-orange', 'ArtemisAssets/pump-orange.png'],
  ['pump-blue', 'ArtemisAssets/pump-blue.png'],
  ['pump-purple', 'ArtemisAssets/pump-purple.png'],
  ['pipe-orange', 'ArtemisAssets/pipe-orange.png'],
  ['pipe-blue', 'ArtemisAssets/pipe-blue.png'],
  ['pipe-purple', 'ArtemisAssets/pipe-purple.png'],
  ['keypad', 'ArtemisAssets/keypad.png'],
  ['hand', 'ArtemisAssets/hand.png'],
  // ui (each CTA has a charcoal hover-state texture — same dims as its base)
  ['play-button', 'ArtemisAssets/play-button.png'],
  ['play-button-hover', 'ArtemisAssets/play-button-hover.png'],
  ['launch-button', 'ArtemisAssets/launch-button.png'],
  ['launch-button-hover', 'ArtemisAssets/launch-button-hover.png'],
  ['play-again-button', 'ArtemisAssets/play-again-button.png'],
  ['reward-helmet', 'ArtemisAssets/reward-helmet.png'],
  ['spotlight-cone', 'ArtemisAssets/spotlight-cone.png'],
  ['warp-overlay', 'ArtemisAssets/warp-overlay.png'],
  ['next-button', 'ArtemisAssets/next-button.png'],
  ['next-button-hover', 'ArtemisAssets/next-button-hover.png'],
  // hint system (pizza-game convention — both 2x exports)
  ['hint-button', 'ArtemisAssets/hint-button.png'],
  ['hint-close', 'ArtemisAssets/hint-close.png'],
];

// Sound effects + background music.
const AUDIO = [
  ['bgm', 'Audio/bgm.mp3'], // sci-fi loop, starts on Play click
  ['correct-choice', 'Audio/correct-choice.mp3'], // per correct pump answer
  ['correct-answer', 'Audio/correct-answer.mp3'], // round solved
  ['wrong-buzz', 'Audio/wrong-buzz.mp3'], // wrong answer
  ['bar-completed', 'Audio/bar-completed.mp3'], // percentage bar hits 100%
  ['liquid-whoosh', 'Audio/liquid-whoosh.mp3'], // bar segment fill
  ['rocket-launch', 'Audio/rocket-launch.mp3'],
  ['rocket-landing', 'Audio/rocket-landing.mp3'],
];

export default class LoadScene extends Phaser.Scene {
  constructor() {
    super({ key: 'LoadScene' });
  }

  async preload() {
    this.load.json('loading-anim', 'assets/loadAnims/cuemath_loading.json');
    await loadFonts();
  }

  create() {
    this.cameras.main.setBackgroundColor('#0e0f1a');
    this.loader = this.buildLottieLoader();

    this.load.on('loaderror', file => {
      console.warn('[LoadScene] missing asset:', file.key, file.src);
    });

    if (this.loader && this.loader.anim) {
      this.loader.anim.playSegments([0, 25], true);
    }

    // Cache-bust image loads so a swapped PNG on disk doesn't show stale.
    const v = `v=${Date.now()}`;
    ASSETS.forEach(([key, file]) => this.load.image(key, `assets/${file}?${v}`));

    AUDIO.forEach(([key, file]) => this.load.audio(key, `assets/${file}`));

    // Rocket launch animation (multi-page atlas, frames authored full-frame).
    this.load.multiatlas('rocketAnims', 'assets/rocketAnims/rocketAnims.json', 'assets/rocketAnims');
    // Pump hose animations (orange/blue/purple — 40 frames each, full-frame).
    this.load.multiatlas('pumpAnims', 'assets/pumpAnims/pumpAnims.json', 'assets/pumpAnims');

    // Hyperdrive transition videos (between-round galaxy warp).
    this.load.video('hyperdriveStart', 'assets/ArtemisAssets/HYPERDRIVE_BEGINING.mp4');
    this.load.video('hyperdriveEnd', 'assets/ArtemisAssets/HYPERDRIVE_end.mp4');

    // Reward-reveal multiatlases (ported from pizza-game / truck-loading).
    this.load.multiatlas('baseCircle', 'assets/anims/rewardAnims/baseCircleAnim.json', 'assets/anims/rewardAnims');
    this.load.multiatlas('rewardLight', 'assets/anims/rewardAnims/rewardLightAnim.json', 'assets/anims/rewardAnims');
    this.load.multiatlas('glitter', 'assets/anims/rewardAnims/glitterAnim.json', 'assets/anims/rewardAnims');

    this.load.start();

    this.load.on('complete', () => {
      const moveOn = () => {
        this.teardownLoader();
        this.scene.stop('LoadScene');
        this.scene.start('StartScene');
      };

      if (this.loader && this.loader.anim) {
        this.loader.anim.loop = false;
        this.loader.anim.playSegments([26, 41], true);
        this.loader.anim.addEventListener('complete', moveOn);
        this.time.delayedCall(1500, () => {
          if (this.loader) moveOn();
        });
      } else {
        moveOn();
      }
    });
  }

  buildLottieLoader() {
    if (!this.cache.json.exists('loading-anim')) return null;

    const cx = this.cameras.main.width / 2;
    const cy = this.cameras.main.height / 2;
    const W_BOX = 400;
    const H_BOX = 400;

    const dom = this.add.dom(cx, cy, 'div').setOrigin(0.5).setScale(0.7);
    const node = dom.node;
    node.style.width = `${W_BOX}px`;
    node.style.height = `${H_BOX}px`;
    node.style.marginLeft = `${-W_BOX / 2}px`;
    node.style.marginTop = `${-H_BOX / 2}px`;
    node.style.pointerEvents = 'none';

    const anim = lottie.loadAnimation({
      container: node,
      renderer: 'svg',
      loop: true,
      autoplay: false,
      animationData: this.cache.json.get('loading-anim'),
    });

    return { dom, anim };
  }

  teardownLoader() {
    if (!this.loader) return;
    try {
      if (this.loader.anim) {
        this.loader.anim.stop();
        this.loader.anim.destroy();
      }
      if (this.loader.dom && this.loader.dom.node) this.loader.dom.node.innerHTML = '';
      if (this.loader.dom) this.loader.dom.destroy();
    } catch (_e) {
      /* best-effort */
    }
    this.loader = null;
  }
}
